<template>
  <el-drawer
    v-model="visible"
    direction="rtl"
    size="min(100vw, clamp(720px, 72vw, 960px))"
    :with-header="false"
    append-to-body
    class="auth-drawer"
  >
    <div class="auth-drawer-shell" :class="{ 'is-register-mode': mode === 'register', 'is-login-mode': mode === 'login' }">
      <aside class="auth-intro-panel">
        <div class="auth-intro-copy">
          <div class="auth-kicker auth-intro-kicker">{{ copy.introKicker }}</div>
          <h2>
            {{ copy.introTitleTop }}<br />
            <span>{{ copy.introTitleBottom }}</span>
          </h2>
          <p>{{ copy.introDesc }}</p>
        </div>

        <div class="auth-stats-row">
          <div class="auth-stat-item">
            <strong>100,000<span>+</span></strong>
            <small>{{ copy.statSku }}</small>
          </div>
          <div class="auth-stat-divider"></div>
          <div class="auth-stat-item">
            <strong>500<span>+</span></strong>
            <small>{{ copy.statOrg }}</small>
          </div>
          <div class="auth-stat-divider"></div>
          <div class="auth-stat-item">
            <strong>24h</strong>
            <small>{{ copy.statSpeed }}</small>
          </div>
        </div>

        <div class="auth-feature-list">
          <div class="auth-feature-item">
            <span class="auth-feature-icon is-success"><el-icon><CircleCheck /></el-icon></span>
            <span>{{ copy.featureQuality }}</span>
          </div>
          <div class="auth-feature-item">
            <span class="auth-feature-icon is-warning">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M13 2 3 14h8l-1 8 11-13h-8l1-7Z" />
              </svg>
            </span>
            <span>{{ copy.featureStock }}</span>
          </div>
          <div class="auth-feature-item">
            <span class="auth-feature-icon is-primary"><el-icon><CreditCard /></el-icon></span>
            <span>{{ copy.featureAccount }}</span>
          </div>
          <div class="auth-feature-item">
            <span class="auth-feature-icon is-info"><el-icon><Promotion /></el-icon></span>
            <span>{{ copy.featureLicense }}</span>
          </div>
        </div>
      </aside>

      <section class="auth-form-panel" :class="{ 'is-register-mode': mode === 'register', 'is-login-mode': mode === 'login' }">
        <div class="auth-drawer-top">
          <button class="auth-close-btn" type="button" :aria-label="copy.closeLabel" @click="close">
            <el-icon><Close /></el-icon>
          </button>
          <div class="auth-kicker">{{ copy.kicker }}</div>
          <h2>{{ mode === 'login' ? copy.loginTitle : copy.registerTitle }}</h2>
          <p>{{ mode === 'login' ? copy.loginDesc : copy.registerDesc }}</p>
        </div>

        <div class="auth-drawer-panel" :class="{ 'is-register': mode === 'register', 'is-login': mode === 'login' }">
          <transition name="el-fade-in-linear" mode="out-in">
            <LoginForm
              v-if="mode === 'login'"
              :redirect-on-success="Boolean(redirectTo)"
              :redirect-to="redirectTo"
              @login-success="close"
              @onRegister="switchMode('register')"
            />
            <RegisterForm
              v-else
              @onLogin="switchMode('login')"
            />
          </transition>
        </div>
      </section>
    </div>
  </el-drawer>
</template>

<script setup>
import { ref } from 'vue';
import { CircleCheck, Close, CreditCard, Promotion } from '@element-plus/icons-vue';
import LoginForm from './LoginForm.vue';
import RegisterForm from './RegisterForm.vue';

const visible = ref(false);
const mode = ref('login');
const redirectTo = ref('');
const copy = {
  closeLabel: '\u5173\u95ed',
  kicker: '\u666e\u6d1b\u6c47\u4e13\u5c5e\u91c7\u8d2d\u5e73\u53f0',
  loginTitle: '\u6b22\u8fce\u56de\u6765',
  registerTitle: '\u521b\u5efa\u4e13\u5c5e\u8d26\u53f7',
  loginDesc: '\u767b\u5f55\u540e\u53ef\u67e5\u770b\u8ba2\u5355\u3001\u8d2d\u7269\u8f66\u548c\u4e13\u5c5e\u62a5\u4ef7\u3002',
  registerDesc: '\u586b\u5199\u771f\u5b9e\u673a\u6784\u4fe1\u606f\uff0c\u83b7\u5f97\u7cbe\u51c6\u4ef7\u683c\u4e0e\u5f00\u7968\u670d\u52a1\u3002',
  introKicker: '\u4f01\u4e1a\u7ea7\u79d1\u7814\u91c7\u8d2d\u5e73\u53f0',
  introTitleTop: '\u7cbe\u51c6\u8bd5\u5242\uff0c',
  introTitleBottom: '\u8d4b\u80fd\u6bcf\u4e00\u6b21\u4f1f\u5927\u53d1\u73b0',
  introDesc: '\u666e\u6d1b\u6c47\u751f\u7269\u79d1\u6280\u4e13\u6ce8\u4e8e\u79d1\u7814\u8bd5\u5242\u53ca\u914d\u5957\u8017\u6750\u7684\u5e73\u53f0\u578b\u670d\u52a1\uff0c\u4ea7\u54c1\u6db5\u76d6\u9ad8\u7aef\u5316\u5b66\u54c1\u3001\u5206\u6790\u8272\u8c31\u8bd5\u5242\u3001\u751f\u7269\u7f13\u51b2\u5242\u7b49\u591a\u4e2a\u9886\u57df\u3002',
  statSku: '\u4f18\u8d28\u8bd5\u5242\u54c1\u7c7b',
  statOrg: '\u5408\u4f5c\u79d1\u7814\u673a\u6784',
  statSpeed: '\u6781\u901f\u53d1\u8d27\u54cd\u5e94',
  featureQuality: '\u4e25\u82db\u7684\u8d28\u91cf\u63a7\u5236\u4f53\u7cfb',
  featureStock: '\u73b0\u8d27\u76f4\u53d1\uff0c\u6781\u901f\u54cd\u5e94',
  featureAccount: '\u652f\u6301\u5bf9\u516c\u7ed3\u7b97\u4e0e\u8d26\u671f\u652f\u4ed8',
  featureLicense: '\u5371\u5316\u54c1\u8d44\u8d28\u9f50\u5168\uff0c\u5408\u89c4\u914d\u9001',
};

const switchMode = (type) => {
  mode.value = type === 'register' ? 'register' : 'login';
};

const open = (type = 'login', redirect = '') => {
  mode.value = type === 'register' ? 'register' : 'login';
  redirectTo.value = redirect || '';
  visible.value = true;
};

const close = () => {
  visible.value = false;
};

defineExpose({
  open,
  close,
});
</script>

<style scoped>
:global(.auth-drawer .el-drawer__body) {
  height: 100%;
  padding: 0;
  overflow: hidden;
}

:global(.auth-drawer) {
  max-width: 100vw;
  height: 100dvh !important;
}

.auth-drawer-shell {
  display: grid;
  grid-template-columns: minmax(320px, 42%) minmax(0, 1fr);
  height: 100dvh;
  max-height: 100dvh;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
}

.auth-intro-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0;
  min-height: 0;
  padding:
    clamp(34px, 5.6vh, 54px)
    clamp(28px, 3.2vw, 44px);
  overflow-y: auto;
  overflow-x: hidden;
  color: #ffffff;
  background:
    radial-gradient(circle at 22% 12%, rgba(0, 180, 216, 0.2), transparent 34%),
    radial-gradient(circle at 86% 78%, rgba(0, 119, 182, 0.24), transparent 36%),
    linear-gradient(135deg, #021b33 0%, #032541 42%, #0a3d62 100%);
  scrollbar-width: none;
}

.auth-intro-panel::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.auth-intro-panel::-webkit-scrollbar-thumb {
  border: 2px solid rgba(3, 37, 65, 0.9);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.26);
}

.auth-intro-panel::-webkit-scrollbar-track {
  background: transparent;
}

.auth-intro-copy {
  position: relative;
  z-index: 1;
}

.auth-intro-kicker {
  margin-bottom: clamp(16px, 3vh, 24px);
}

.auth-intro-copy h2 {
  margin: 0 0 18px;
  font-size: clamp(28px, 3vw, 36px);
  font-weight: 900;
  line-height: 1.28;
  letter-spacing: 0;
}

.auth-intro-copy h2 span {
  color: #29c9ee;
}

.auth-intro-copy p {
  max-width: 380px;
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 14px;
  line-height: 1.85;
}

.auth-stats-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-items: center;
  margin: clamp(22px, 5vh, 36px) 0 22px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.auth-stat-item {
  min-width: 0;
  padding: 10px 6px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  text-align: center;
}

.auth-stat-item:first-child {
  grid-column: 1 / -1;
}

.auth-stat-item strong {
  display: block;
  color: #ffffff;
  font-size: clamp(20px, 2vw, 26px);
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.auth-stat-item strong span {
  color: #00b4d8;
  font-size: 0.74em;
}

.auth-stat-item small {
  display: block;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 11px;
  line-height: 1.3;
}

.auth-stat-divider {
  display: none;
}

.auth-feature-list {
  display: grid;
  gap: 12px;
  margin-top: 0;
}

.auth-feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  line-height: 1.5;
}

.auth-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.auth-feature-icon svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.auth-feature-icon.is-success {
  color: #34d399;
}

.auth-feature-icon.is-warning {
  color: #fbbf24;
}

.auth-feature-icon.is-primary {
  color: #38bdf8;
}

.auth-feature-icon.is-info {
  color: #a78bfa;
}

.auth-form-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
  box-shadow: inset 1px 0 0 #e5e7eb;
}

.auth-drawer-top {
  position: relative;
  flex: 0 0 auto;
  padding:
    clamp(42px, 7vh, 66px)
    clamp(36px, 4.8vw, 66px)
    clamp(14px, 2.4vh, 20px);
  padding-right: 82px;
  background: #ffffff;
  color: #111827;
}

.auth-close-btn {
  position: absolute;
  top: clamp(14px, 2.4vh, 18px);
  right: clamp(14px, 2.4vw, 20px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.auth-close-btn:hover {
  border-color: rgba(0, 137, 238, 0.28);
  background: #eff6ff;
  color: #0369a1;
}

.auth-kicker {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-bottom: clamp(10px, 2vh, 16px);
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bdefff;
  font-size: 12px;
  font-weight: 700;
}

.auth-drawer-top h2 {
  margin: 0 0 8px;
  color: #0f172a;
  font-size: clamp(26px, 2.4vw, 34px);
  font-weight: 900;
  line-height: 1.25;
  letter-spacing: 0;
}

.auth-drawer-top p {
  max-width: 420px;
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

.auth-drawer-top .auth-kicker {
  border-color: #bae6fd;
  background: #f0f9ff;
  color: #0369a1;
}

.auth-drawer-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding:
    clamp(18px, 3vh, 24px)
    clamp(36px, 4.8vw, 66px)
    clamp(28px, 5vh, 44px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
}

.auth-drawer-panel.is-register {
  overflow: hidden;
}

.auth-drawer-panel.is-login {
  overflow-y: auto;
}

.auth-form-panel.is-register-mode .auth-drawer-top {
  padding-top: clamp(24px, 4vh, 34px);
  padding-bottom: 8px;
}

.auth-form-panel.is-register-mode .auth-drawer-top .auth-kicker {
  margin-bottom: 10px;
  padding: 5px 11px;
}

.auth-form-panel.is-register-mode .auth-drawer-top h2 {
  margin-bottom: 4px;
  font-size: clamp(24px, 2vw, 30px);
}

.auth-form-panel.is-register-mode .auth-drawer-top p {
  line-height: 1.45;
}

.auth-drawer-panel.is-register {
  padding-top: clamp(8px, 1.6vh, 12px);
  padding-bottom: 16px;
}

.auth-drawer-panel.is-register :deep(.register-form-head > .mb-2) {
  display: none;
}

.auth-drawer-panel.is-register :deep(.register-tip) {
  margin-bottom: 10px !important;
  padding: 7px 10px !important;
  font-size: 12px;
  line-height: 1.45;
}

.auth-drawer-panel.is-register :deep(.el-form-item) {
  margin-bottom: 12px !important;
}

.auth-drawer-panel.is-register :deep(.el-form-item__label) {
  padding-bottom: 3px;
  line-height: 1.3;
}

.auth-drawer-panel.is-register :deep(.register-footer) {
  gap: 8px;
  padding-top: 10px;
}

.auth-drawer-panel.is-register :deep(.register-submit-btn) {
  height: 42px !important;
}

@media (min-width: 761px) and (min-height: 820px) {
  .auth-drawer-panel.is-register :deep(.register-form) {
    flex: 0 0 auto;
    min-height: auto;
  }

  .auth-drawer-panel.is-register :deep(.register-scroll-area) {
    flex: 0 0 auto;
    overflow: visible;
    padding-right: 0 !important;
    margin-right: 0 !important;
  }

  .auth-drawer-panel.is-register :deep(.register-footer) {
    position: static;
    margin-top: 12px;
    padding-top: 14px;
  }
}

.auth-drawer-panel::-webkit-scrollbar,
.auth-drawer-panel :deep(.custom-scrollbar::-webkit-scrollbar) {
  width: 8px;
}

.auth-drawer-panel::-webkit-scrollbar-thumb,
.auth-drawer-panel :deep(.custom-scrollbar::-webkit-scrollbar-thumb) {
  border: 2px solid #ffffff;
  border-radius: 999px;
  background: #cbd5e1;
}

.auth-drawer-panel::-webkit-scrollbar-track,
.auth-drawer-panel :deep(.custom-scrollbar::-webkit-scrollbar-track) {
  background: transparent;
}

.auth-drawer-panel :deep(.login-wrapper) {
  flex: 1 1 auto;
  align-items: flex-start;
  min-width: 0;
}

.auth-drawer-panel :deep(.login-form-container) {
  width: 100%;
  max-width: none;
  padding: 0;
  min-width: 0;
}

.auth-drawer-panel :deep(.custom-tabs) {
  gap: clamp(22px, 5vw, 34px);
  flex-wrap: wrap;
  row-gap: 10px;
}

.auth-drawer-panel :deep(.el-form-item) {
  margin-bottom: clamp(16px, 2.6vh, 22px);
}

.auth-drawer-panel :deep(.register-form-shell) {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

.auth-drawer-panel :deep(.register-scroll-area) {
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
}

.auth-drawer-panel :deep(.code-input-group) {
  display: flex;
  gap: 12px;
}

.auth-drawer-panel :deep(.code-input-group .el-input) {
  flex: 1;
  min-width: 0;
}

@media (max-height: 720px) {
  .auth-drawer-top {
    padding-top: 22px;
    padding-bottom: 18px;
  }

  .auth-drawer-top h2 {
    font-size: 24px;
  }

  .auth-drawer-top p {
    line-height: 1.5;
  }

  .auth-drawer-panel {
    padding-top: 18px;
    padding-bottom: 22px;
  }

  .auth-drawer-panel :deep(.login-header) {
    margin-bottom: 24px;
  }

  .auth-drawer-panel :deep(.form-options) {
    margin-bottom: 24px;
  }

  .auth-drawer-panel :deep(.form-buttons) {
    gap: 12px;
  }
}

@media (max-height: 600px) {
  .auth-drawer-top {
    padding-top: 18px;
    padding-bottom: 14px;
  }

  .auth-kicker {
    margin-bottom: 8px;
    padding: 5px 10px;
  }

  .auth-drawer-panel {
    padding-top: 14px;
    padding-bottom: 18px;
  }
}

@media (max-width: 760px) {
  :global(.auth-drawer) {
    width: 100vw !important;
  }

  .auth-drawer-shell {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
    background: #f6f9fc;
  }

  .auth-intro-panel {
    max-height: none;
    min-height: 116px;
    padding: 18px 22px 16px;
    overflow: hidden;
    border-radius: 0 0 18px 18px;
    box-shadow: 0 10px 28px rgba(2, 27, 51, 0.2);
  }

  .auth-intro-copy h2 {
    margin-bottom: 0;
    font-size: 22px;
    line-height: 1.28;
  }

  .auth-intro-copy p {
    display: none;
  }

  .auth-stats-row {
    display: none;
  }

  .auth-intro-kicker {
    margin-bottom: 10px;
    padding: 5px 10px;
    font-size: 11px;
  }

  .auth-form-panel {
    min-height: 0;
    background: #f6f9fc;
    box-shadow: none;
  }

  .auth-feature-list {
    display: none;
  }

  .auth-drawer-top {
    padding: 18px 22px 10px;
    padding-right: 66px;
    background: transparent;
  }

  .auth-drawer-top h2 {
    font-size: 24px;
    margin-bottom: 6px;
  }

  .auth-drawer-top p {
    font-size: 12px;
    line-height: 1.55;
  }

  .auth-drawer-top .auth-kicker {
    margin-bottom: 10px;
    padding: 5px 10px;
    font-size: 11px;
  }

  .auth-drawer-panel {
    padding: 10px 18px 18px;
    background: transparent;
    scrollbar-gutter: auto;
  }

  .auth-drawer-panel :deep(.login-form-container),
  .auth-drawer-panel :deep(.register-form-shell) {
    overflow: hidden;
    border: 1px solid #e5edf5;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
  }

  .auth-drawer-panel :deep(.login-form-container) {
    padding: 20px 18px 18px;
  }

  .auth-drawer-panel :deep(.register-form-shell) {
    padding: 14px 12px 0;
  }

  .auth-drawer-panel.is-register {
    overflow-y: auto;
    padding-top: 8px;
  }

  .auth-drawer-panel.is-register :deep(.register-scroll-area) {
    padding-right: 6px !important;
    margin-right: 0 !important;
  }

  .auth-drawer-panel.is-register :deep(.register-footer) {
    margin: 0 -12px;
    padding: 12px 12px 10px;
  }

  .auth-drawer-shell.is-register-mode .auth-intro-panel {
    min-height: 78px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .auth-drawer-shell.is-register-mode .auth-intro-kicker {
    margin-bottom: 6px;
  }

  .auth-drawer-shell.is-register-mode .auth-intro-copy h2 {
    font-size: 18px;
    line-height: 1.22;
  }

  .auth-form-panel.is-register-mode .auth-drawer-top {
    padding-top: 12px;
    padding-bottom: 6px;
  }

  .auth-form-panel.is-register-mode .auth-drawer-top h2 {
    font-size: 22px;
  }

  .auth-form-panel.is-register-mode .auth-drawer-top p {
    display: none;
  }

  .auth-drawer-panel::-webkit-scrollbar,
  .auth-drawer-panel :deep(.custom-scrollbar::-webkit-scrollbar) {
    width: 4px;
  }

  .auth-drawer-panel::-webkit-scrollbar-thumb,
  .auth-drawer-panel :deep(.custom-scrollbar::-webkit-scrollbar-thumb) {
    border: 0;
    background: #d6e1ec;
  }
}

@media (max-width: 560px) {
  .auth-intro-panel {
    min-height: 108px;
    padding: 16px 18px 14px;
  }

  .auth-intro-kicker {
    margin-bottom: 8px;
  }

  .auth-drawer-top {
    padding: 16px 18px 8px;
    padding-right: 66px;
  }

  .auth-drawer-panel {
    padding: 8px 14px 14px;
  }

  .auth-drawer-panel :deep(.login-form-container) {
    padding: 18px 14px 16px;
    border-radius: 16px;
  }

  .auth-drawer-panel :deep(.register-form-shell) {
    border-radius: 16px;
  }

  .auth-drawer-shell.is-register-mode .auth-intro-panel {
    min-height: 72px;
  }

}

@media (max-width: 560px) and (max-height: 760px) {
  .auth-intro-panel {
    min-height: 96px;
  }

  .auth-intro-copy p {
    display: none;
  }

  .auth-stats-row {
    margin-top: 12px;
  }
}

@media (max-width: 420px) {
  .auth-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: #ffffff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }

  .auth-drawer-top {
    padding-left: 18px;
    padding-right: 60px;
  }

  .auth-drawer-panel {
    padding-right: 14px;
    padding-left: 14px;
  }

  .auth-intro-panel {
    min-height: 92px;
    padding-right: 18px;
    padding-left: 18px;
  }

  .auth-intro-copy p,
  .auth-stats-row {
    display: none;
  }

  .auth-intro-copy h2 {
    font-size: 20px;
    margin-bottom: 0;
  }

  .auth-drawer-shell.is-register-mode .auth-intro-copy h2 {
    font-size: 17px;
  }

  .auth-drawer-panel :deep(.code-input-group) {
    flex-direction: column;
    gap: 8px;
  }

  .auth-drawer-panel :deep(.code-input-group .el-button) {
    width: 100%;
    height: 38px;
    min-width: 0;
    flex: 0 0 auto;
    margin-left: 0;
  }
}
</style>
